import {
  INITIATIVE_STATUS_LABELS,
  TRAFFIC_LIGHT_LABELS,
  TREND_LABELS,
  executiveDashboardSchema,
  formatDate,
  formatDateTime,
  formatDecimal,
  targetPeriodLabel,
  type ExecutiveDashboard,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  FolderKanban,
  Minus,
  type LucideIcon,
  AlertTriangle,
  Gauge,
  ListChecks,
  Rocket,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { STATUS_DOT, StatusBadge } from '@/components/status/StatusBadge';
import { NativeSelect } from '@/components/ui/select';
import { actionLink } from '@/features/meetings/CriticalAnalysis';
import { drillHref, perspectiveTarget, usePeriodParam, worstPerspective } from '@/features/drill/drill';
import { DRILL_NUMBER, DrillValueLink } from '@/features/drill/DrillLinks';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { useAuth, useCan } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const pct = (v: number | null) => (v === null ? '—' : `${formatDecimal(v, 1)}%`);
const monthLabel = (year: number, month: number) => `${targetPeriodLabel('MONTH', month)}/${year}`;

/**
 * Dashboard executivo (T-141b, RF-34, RNF-14; DEC-055): a saúde da estratégia "em poucos segundos" — performance
 * estratégica com a tendência e as perspectivas no topo (wireframe do §22), objetivos críticos ao lado do previsto x
 * realizado do ano, tendência geral, KPIs críticos, ações atrasadas, iniciativas em risco e os 5 melhores e 5 piores
 * KPIs; cada item leva ao detalhe.
 */
export function DashboardExecutivoPage() {
  const { organizationId = '' } = useParams();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer>
      <PageHeader
        title="Dashboard executivo"
        description="A saúde da estratégia no mês: performance, tendência, exceções e os melhores e piores indicadores."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O dashboard mostra a estratégia de um projeto. Quando a empresa tiver um projeto, ele aparece aqui."
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-6">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          <Dashboard organizationId={organizationId} project={project} />
        </div>
      )}
    </PageContainer>
  );
}

function Dashboard({
  organizationId,
  project,
}: {
  organizationId: string;
  project: { id: string; horizonStart: number; horizonEnd: number };
}) {
  const projectId = project.id;
  const { client } = useAuth();
  const ids = { year: useId(), month: useId() };
  const can = useCan();
  // null = o período padrão da API (o último mês com dado); o escolhido (ou do link) fica no endereço (T-144c).
  const [period, setPeriod] = usePeriodParam([project.horizonStart, project.horizonEnd]);
  const query = useQuery({
    queryKey: ['executive-dashboard', projectId, period],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${projectId}/executive-dashboard`, {
        query: period ? { year: String(period.year), month: String(period.month) } : {},
        schema: executiveDashboardSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  if (query.isPending) return <LoadingState rows={6} label="Carregando o dashboard" />;
  const d = query.data;
  const shown = period ?? (d ? { year: d.year, month: d.month } : null);
  const updating = query.isFetching && query.isPlaceholderData;
  const years = Array.from(
    { length: project.horizonEnd - project.horizonStart + 1 },
    (_, i) => project.horizonStart + i,
  );
  const error = (
    <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
  );
  // Erro no primeiro carregamento: só o aviso. Erro depois de trocar o período: o seletor continua para escolher outro.
  if (!shown) return error;

  const periodBar = (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
          Mês
        </label>
        <NativeSelect
          id={ids.month}
          value={shown.month}
          onChange={(e) => setPeriod({ year: shown.year, month: Number(e.target.value) })}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {targetPeriodLabel('MONTH', i + 1)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <div>
        <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
          Ano
        </label>
        <NativeSelect
          id={ids.year}
          value={shown.year}
          onChange={(e) => setPeriod({ year: Number(e.target.value), month: shown.month })}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </NativeSelect>
      </div>
      <p className="pb-2 text-xs text-muted-foreground" aria-live="polite">
        {updating
          ? `Atualizando para ${monthLabel(shown.year, shown.month).toLowerCase()}…`
          : !d || query.isError
            ? null
            : d.computedAt
              ? `Dados de ${monthLabel(d.year, d.month).toLowerCase()}, calculados em ${formatDateTime(d.computedAt)}`
              : `Ainda não há performance calculada em ${monthLabel(d.year, d.month).toLowerCase()}`}
      </p>
    </div>
  );
  if (query.isError || !d) {
    return (
      <div className="flex flex-col gap-6">
        {periodBar}
        {error}
      </div>
    );
  }
  const sees = { dashboard: can('dashboard-perspectiva', 'view'), objectives: can('objetivos', 'view') };
  const perspectiveTo = (perspectiveId: string) =>
    perspectiveTarget(
      organizationId,
      { projectId: d.project.id, perspectiveId, year: d.year, month: d.month },
      sees,
    );
  const worst = worstPerspective(d.perspectives);
  const companyTo = worst ? perspectiveTo(worst.id) : null;

  return (
    <div className="flex flex-col gap-6" aria-busy={query.isFetching}>
      {periodBar}

      {/* Topo do wireframe (§22): performance estratégica com a tendência e as perspectivas. Cada número leva ao
          nível de baixo no mesmo mês (drill-down — T-144c, RF-37). */}
      <section
        aria-label="Performance estratégica"
        className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
      >
        <div className="min-w-0 rounded-lg border border-border bg-surface p-5">
          <h2 className="text-sm font-semibold text-muted-foreground">Performance estratégica</h2>
          {d.company && d.company.performance !== null ? (
            <>
              <p className="mt-2 flex items-center gap-3">
                {companyTo && worst ? (
                  <Link
                    to={companyTo}
                    className={cn('text-4xl font-extrabold tabular-nums', DRILL_NUMBER)}
                    aria-label={`Performance estratégica ${pct(d.company.performance)}: aprofundar em ${worst.name}, a perspectiva de menor performance`}
                  >
                    {pct(d.company.performance)}
                  </Link>
                ) : (
                  <span className="text-4xl font-extrabold tabular-nums">{pct(d.company.performance)}</span>
                )}
                <StatusBadge status={d.company.light} />
              </p>
              <Trend value={d.company.trend} />
              <p className="mt-2 text-sm text-muted-foreground">
                Acumulado do ano:{' '}
                {companyTo && worst ? (
                  <Link
                    to={companyTo}
                    className={cn('font-semibold text-foreground tabular-nums', DRILL_NUMBER)}
                    aria-label={`Acumulado do ano ${pct(d.company.ytdPerformance)}: aprofundar em ${worst.name}`}
                  >
                    {pct(d.company.ytdPerformance)}
                  </Link>
                ) : (
                  <span className="font-semibold text-foreground tabular-nums">
                    {pct(d.company.ytdPerformance)}
                  </span>
                )}
              </p>
              {companyTo && worst ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Os números sublinhados abrem o nível de baixo; a empresa leva a {worst.name}, a perspectiva
                  de menor performance.
                </p>
              ) : null}
            </>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Sem performance da empresa neste mês: ela aparece quando há metas e resultados lançados.
            </p>
          )}
        </div>
        <div className="min-w-0 rounded-lg border border-border bg-surface p-5">
          <h2 className="text-sm font-semibold text-muted-foreground">Perspectivas</h2>
          {d.perspectives.some((p) => p.performance !== null) ? (
            <table className="mt-2 w-full text-sm">
              <caption className="sr-only">Performance e status das perspectivas</caption>
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="py-1 text-left font-medium">
                    Perspectiva
                  </th>
                  <th scope="col" className="py-1 text-right font-medium">
                    Performance
                  </th>
                  <th scope="col" className="py-1 pl-3 text-left font-medium">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.perspectives.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <th scope="row" className="py-2 pr-2 text-left font-medium break-words">
                      {perspectiveTo(p.id) ? (
                        <Link
                          to={perspectiveTo(p.id)!}
                          className="text-brand-strong underline-offset-2 hover:underline dark:text-brand"
                        >
                          {p.name}
                        </Link>
                      ) : (
                        p.name
                      )}
                    </th>
                    <td className="py-2 text-right tabular-nums">
                      {perspectiveTo(p.id) ? (
                        <Link
                          to={perspectiveTo(p.id)!}
                          className={cn('font-bold', DRILL_NUMBER)}
                          aria-label={`${p.name}: ${pct(p.performance)}`}
                        >
                          {pct(p.performance)}
                        </Link>
                      ) : (
                        <span className="font-bold">{pct(p.performance)}</span>
                      )}
                      <Trend value={p.trend} compact />
                    </td>
                    <td className="py-2 pl-3">
                      <StatusBadge status={p.light} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">Sem performance das perspectivas neste mês.</p>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <ObjectivesBlock d={d} organizationId={organizationId} />
        <PlannedChart d={d} />
      </div>

      <GeneralTrend d={d} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <KpiBlock
          title="KPIs críticos"
          icon={Gauge}
          block={d.criticalKpis}
          d={d}
          organizationId={organizationId}
          empty="Nenhum KPI vermelho no mês."
          critical
        />
        <LateActionsBlock d={d} organizationId={organizationId} />
        <InitiativesBlock d={d} organizationId={organizationId} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <KpiBlock
          title="Top 5 melhores desempenhos"
          icon={ThumbsUp}
          block={d.bestKpis}
          d={d}
          organizationId={organizationId}
          empty="Nenhum KPI com performance no mês."
          ranked
        />
        <KpiBlock
          title="Top 5 piores desempenhos"
          icon={ThumbsDown}
          block={d.worstKpis}
          d={d}
          organizationId={organizationId}
          empty="Nenhum KPI com performance no mês."
          ranked
        />
      </div>
    </div>
  );
}

function Trend({ value, compact = false }: { value: number | null; compact?: boolean }) {
  if (value === null) {
    return compact ? null : (
      <p className="mt-1 text-sm text-muted-foreground">Sem o mês anterior para comparar.</p>
    );
  }
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  return (
    <span
      className={cn(
        'flex items-center gap-1 tabular-nums',
        compact ? 'justify-end text-xs' : 'mt-1 text-sm',
        value > 0 ? 'text-status-green-fg' : value < 0 ? 'text-status-red-fg' : 'text-muted-foreground',
      )}
    >
      <Icon className="size-4" aria-hidden />
      {`${value > 0 ? '+' : ''}${formatDecimal(value, 1)} p.p.`}
      {compact ? <span className="sr-only"> vs mês anterior</span> : ' vs mês anterior'}
    </span>
  );
}

/** Bloco com título, total e até 5 itens; null = sem acesso ao módulo de origem (não aparece). */
function Block({
  title,
  icon: Icon,
  total,
  empty,
  children,
}: {
  title: string;
  icon: LucideIcon;
  total: number;
  empty: string;
  children: ReactNode;
}) {
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-surface p-4"
    >
      <h2 id={titleId} className="flex items-center gap-2 text-sm font-bold">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
        {title}
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">{total}</span>
      </h2>
      {total === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : children}
    </section>
  );
}

type Row = {
  key: string;
  title: string;
  detail: string;
  to: string | null;
  tone: TrafficLight;
  value?: string;
  /** Destino próprio do número (o nível de baixo — drill-down, T-144c). */
  valueTo?: { to: string; label: string } | null;
};

function Rows({ label, rows, more }: { label: string; rows: Row[]; more: number }) {
  return (
    <>
      <ul aria-label={label} className="flex flex-col divide-y divide-border">
        {rows.map((r) => {
          const content = (
            <>
              <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', STATUS_DOT[r.tone])} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block break-words font-medium">{r.title}</span>
                <span className="block break-words text-xs text-muted-foreground">{r.detail}</span>
              </span>
              {r.value && !r.valueTo ? (
                <span className="shrink-0 font-bold tabular-nums">{r.value}</span>
              ) : null}
            </>
          );
          return (
            <li key={r.key} className="flex items-start gap-2">
              {r.to ? (
                <Link to={r.to} className="flex min-w-0 flex-1 gap-2 rounded-md px-1 py-2 hover:bg-muted/50">
                  {content}
                  {r.valueTo ? null : (
                    <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                </Link>
              ) : (
                <div className="flex min-w-0 flex-1 gap-2 px-1 py-2">{content}</div>
              )}
              {r.value && r.valueTo ? (
                <span className="py-2">
                  <DrillValueLink to={r.valueTo.to} label={r.valueTo.label}>
                    {r.value}
                  </DrillValueLink>
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {more > 0 ? <p className="text-xs text-muted-foreground">e mais {more}</p> : null}
    </>
  );
}

function ObjectivesBlock({ d, organizationId }: { d: ExecutiveDashboard; organizationId: string }) {
  const can = useCan();
  const b = d.criticalObjectives;
  if (!b) return null;
  return (
    <Block
      title="Objetivos críticos"
      icon={AlertTriangle}
      total={b.total}
      empty="Nenhum objetivo vermelho no mês."
    >
      <Rows
        label="Objetivos críticos"
        more={b.total - b.items.length}
        rows={b.items.map((o) => ({
          key: o.id,
          title: o.name,
          detail: o.perspective,
          value: pct(o.performance),
          to: drillHref.objective(organizationId, {
            projectId: d.project.id,
            objectiveId: o.id,
            year: d.year,
            month: d.month,
          }),
          tone: o.light,
        }))}
      />
      {can('mapa-estrategico', 'view') ? (
        <Link
          to={drillHref.map(organizationId, { projectId: d.project.id, year: d.year, month: d.month })}
          className="text-xs font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
        >
          Ver no mapa estratégico
        </Link>
      ) : null}
    </Block>
  );
}

function KpiBlock({
  title,
  icon,
  block,
  d,
  organizationId,
  empty,
  ranked = false,
  critical = false,
}: {
  title: string;
  icon: LucideIcon;
  block: ExecutiveDashboard['criticalKpis'];
  d: ExecutiveDashboard;
  organizationId: string;
  empty: string;
  ranked?: boolean;
  /** Bloco dos KPIs críticos: tem o "ver todos". */
  critical?: boolean;
}) {
  const can = useCan();
  if (!block) return null;
  const seesHistory = can('metas-resultados', 'view');
  // Os que ficaram de fora da lista: os KPIs vermelhos do mês em Resultados (só no bloco dos críticos).
  const all =
    critical && seesHistory
      ? `/empresas/${organizationId}/indicadores/resultados?projeto=${d.project.id}&ano=${d.year}&mes=${d.month}&semaforo=vermelho`
      : null;
  return (
    <Block
      title={title}
      icon={icon}
      total={ranked ? Math.min(block.total, block.items.length) : block.total}
      empty={empty}
    >
      <Rows
        label={title}
        more={ranked ? 0 : block.total - block.items.length}
        rows={block.items.map((k, i) => ({
          key: k.kpiId,
          title: `${ranked ? `${i + 1}. ` : ''}${k.code} · ${k.name}`,
          detail: TRAFFIC_LIGHT_LABELS[k.light],
          value: pct(k.performance),
          to: seesHistory
            ? drillHref.kpi(organizationId, {
                projectId: d.project.id,
                kpiId: k.kpiId,
                year: d.year,
                month: d.month,
              })
            : null,
          valueTo: seesHistory
            ? {
                to: drillHref.unit(organizationId, {
                  projectId: d.project.id,
                  kpiId: k.kpiId,
                  year: d.year,
                  month: d.month,
                }),
                label: `${k.code} ${pct(k.performance)}: ver por unidade`,
              }
            : null,
          tone: k.light,
        }))}
      />
      {all ? (
        <Link
          to={all}
          className="text-xs font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
        >
          Ver os KPIs em vermelho
        </Link>
      ) : null}
    </Block>
  );
}

function LateActionsBlock({ d, organizationId }: { d: ExecutiveDashboard; organizationId: string }) {
  const b = d.lateActions;
  if (!b) return null;
  return (
    <Block title="Ações atrasadas" icon={ListChecks} total={b.total} empty="Nenhuma ação atrasada.">
      <Rows
        label="Ações atrasadas"
        more={b.total - b.items.length}
        rows={b.items.map((a) => ({
          key: a.id,
          title: a.title,
          detail: `${a.planName} · prazo ${formatDate(a.dueDate)}${a.responsibleName ? ` · ${a.responsibleName}` : ''}`,
          to: actionLink(organizationId, d.project.id, a.planId, a.id),
          tone: 'red',
        }))}
      />
      <Link
        to={`/empresas/${organizationId}/execucao/planos-acao?projeto=${d.project.id}&atrasadas=1`}
        className="text-xs font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
      >
        Ver as ações atrasadas
      </Link>
    </Block>
  );
}

function InitiativesBlock({ d, organizationId }: { d: ExecutiveDashboard; organizationId: string }) {
  const b = d.initiativesAtRisk;
  if (!b) return null;
  return (
    <Block
      title="Iniciativas em risco"
      icon={Rocket}
      total={b.total}
      empty="Nenhuma iniciativa em risco ou atrasada."
    >
      <Rows
        label="Iniciativas em risco"
        more={b.total - b.items.length}
        rows={b.items.map((i) => ({
          key: i.id,
          title: i.name,
          detail: `${i.objective} · ${INITIATIVE_STATUS_LABELS[i.status]}${
            i.late && i.endDate ? ` · fim previsto em ${formatDate(i.endDate)} (atrasada)` : ''
          }`,
          to: `/empresas/${organizationId}/execucao/iniciativas?projeto=${d.project.id}&iniciativa=${i.id}`,
          tone: i.status === 'AT_RISK' || i.late ? 'red' : 'yellow',
        }))}
      />
      <Link
        to={`/empresas/${organizationId}/execucao/iniciativas?projeto=${d.project.id}`}
        className="text-xs font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
      >
        Ver as iniciativas
      </Link>
    </Block>
  );
}

/** Previsto x realizado estratégico: a performance da empresa mês a mês (e a acumulada) contra 100% da meta. */
function PlannedChart({ d }: { d: ExecutiveDashboard }) {
  const points = d.plannedVsActual.months.map((m) => ({
    name: targetPeriodLabel('MONTH', m.month),
    realizado: m.performance,
    acumulado: m.ytdPerformance,
    previsto: 100,
  }));
  const withData = points.filter((p) => p.realizado !== null);
  const { greenMin, yellowMin } = d.plannedVsActual.bands;
  const summary = points
    .map(
      (p) =>
        `${p.name}: ${p.realizado === null ? 'sem dados' : pct(p.realizado)}${
          p.acumulado === null ? '' : ` (acumulado ${pct(p.acumulado)})`
        }`,
    )
    .join('; ');
  const tick = { fontSize: 12, fill: 'var(--muted-foreground)' };
  return (
    <section
      aria-labelledby="previsto-realizado"
      className="min-w-0 rounded-lg border border-border bg-surface p-4"
    >
      <h2 id="previsto-realizado" className="text-sm font-bold">
        Previsto x realizado — {d.year}
      </h2>
      <p className="text-xs text-muted-foreground">
        Performance da empresa no mês e acumulada contra o previsto (100% da meta); verde a partir de{' '}
        {pct(greenMin)}, amarelo a partir de {pct(yellowMin)}.
      </p>
      {withData.length ? (
        <div
          className="mt-3 h-64 w-full"
          role="img"
          aria-label={`Performance da empresa em ${d.year} — ${summary}.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="name" tick={tick} />
              <YAxis tick={tick} width={48} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip formatter={(v) => (typeof v === 'number' ? pct(v) : '—')} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={greenMin} stroke="var(--status-green-solid)" strokeDasharray="4 4" />
              <ReferenceLine y={yellowMin} stroke="var(--status-yellow-solid)" strokeDasharray="4 4" />
              <Line
                type="monotone"
                dataKey="previsto"
                name="Previsto"
                stroke="var(--muted-foreground)"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="realizado"
                name="Realizado no mês"
                stroke="var(--brand)"
                strokeWidth={2.5}
                isAnimationActive={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="acumulado"
                name="Acumulado do ano"
                stroke="var(--status-blue-solid)"
                strokeDasharray="5 4"
                isAnimationActive={false}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">Sem performance da empresa em {d.year}.</p>
      )}
    </section>
  );
}

function GeneralTrend({ d }: { d: ExecutiveDashboard }) {
  const t = d.generalTrend;
  const Icon = !t
    ? Minus
    : t.direction === 'UP'
      ? ArrowUpRight
      : t.direction === 'DOWN'
        ? ArrowDownRight
        : Minus;
  return (
    <section aria-labelledby="tendencia-geral" className="rounded-lg border border-border bg-surface p-4">
      <h2 id="tendencia-geral" className="text-sm font-bold">
        Tendência geral
      </h2>
      {t ? (
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <span
            className={cn(
              'inline-flex items-center gap-1 font-bold',
              t.direction === 'UP'
                ? 'text-status-green-fg'
                : t.direction === 'DOWN'
                  ? 'text-status-red-fg'
                  : 'text-muted-foreground',
            )}
          >
            <Icon className="size-4" aria-hidden />
            {TREND_LABELS[t.direction]}
          </span>
          <span className="text-muted-foreground">
            de {pct(t.first.performance)} em {monthLabel(t.first.year, t.first.month).toLowerCase()} para{' '}
            {pct(t.last.performance)} em {monthLabel(t.last.year, t.last.month).toLowerCase()} ({t.months}{' '}
            meses com dado)
          </span>
        </p>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">
          É preciso ao menos dois meses com performance da empresa para mostrar a tendência.
        </p>
      )}
    </section>
  );
}
